import { useState, useEffect, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, FilePdf, ArrowSquareOut, Gear, EnvelopeSimple, WhatsappLogo, MicrosoftOutlookLogo } from "@phosphor-icons/react";
import { PageHeader } from "@/components/PageHeader";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (gridfsId) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${gridfsId}`;

// Tenta partilhar o FICHEIRO real (não o link) através da Web Share API nativa do dispositivo -
// o utilizador escolhe a app (Email, Outlook, WhatsApp...) e o ficheiro vai anexado de verdade.
// Não é possível anexar um ficheiro remoto a um link mailto:/ms-outlook: (limitação da plataforma
// web), por isso quando a partilha nativa não está disponível (ex: browser de computador) o
// ficheiro é descarregado e o utilizador tem de o anexar manualmente.
const shareFileNative = async (url, filename, title) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: title || filename });
      return true;
    }
  } catch (error) {
    if (error?.name === 'AbortError') return true;
    console.error('Partilha nativa falhou:', error);
  }
  return false;
};

const downloadFileForManualAttach = (url, filename, appLabel) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  toast.info(`Ficheiro descarregado - anexe-o manualmente no ${appLabel}`);
};

const catalogFilename = (cat) => cat.title.toLowerCase().endsWith('.pdf') ? cat.title : `${cat.title}.pdf`;

const shareByEmail = async (cat) => {
  const url = fileUrl(cat.gridfs_id);
  const shared = await shareFileNative(url, catalogFilename(cat), `Catálogo: ${cat.title}`);
  if (shared) return;
  downloadFileForManualAttach(url, catalogFilename(cat), 'seu cliente de email');
  window.location.href = `mailto:?subject=${encodeURIComponent(`Catálogo: ${cat.title}`)}`;
};

const shareByOutlook = async (cat) => {
  const url = fileUrl(cat.gridfs_id);
  const shared = await shareFileNative(url, catalogFilename(cat), `Catálogo: ${cat.title}`);
  if (shared) return;
  downloadFileForManualAttach(url, catalogFilename(cat), 'Outlook');
  const subject = encodeURIComponent(`Catálogo: ${cat.title}`);
  const appUrl = `ms-outlook://compose?subject=${subject}`;
  const mailtoUrl = `mailto:?subject=${subject}`;
  const timer = setTimeout(() => {
    if (!document.hidden) {
      window.location.href = mailtoUrl;
    }
  }, 1200);
  document.addEventListener('visibilitychange', () => clearTimeout(timer), { once: true });
  window.location.href = appUrl;
};

const shareByWhatsapp = (cat) => {
  const url = fileUrl(cat.gridfs_id);
  const text = encodeURIComponent(`Catálogo: ${cat.title}\n${url}`);
  window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
};

// Biblioteca pública de Catálogos (substitui o antigo botão único "Catálogo Gresart") -
// grelha com capa + título + botão Abrir, ordenada pela prioridade definida em
// Configurações > Gerir Catálogos (drag & drop, refletido aqui via o campo "order").
export default function Catalogos() {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerCatalog, setViewerCatalog] = useState(null);
  const navigate = useNavigate();

  const fetchCatalogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalogs`);
      setCatalogs(res.data);
    } catch (error) {
      toast.error("Erro ao carregar catálogos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCatalogs(); }, [fetchCatalogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="catalogos-page">
      <PageHeader
        icon={BookOpen}
        title="Catálogos Gresart"
        subtitle="Biblioteca de catálogos em PDF"
      />

      <div className="gresart-card">
        {catalogs.length === 0 ? (
          <div className="empty-state py-16">
            <BookOpen size={64} className="text-stone-300 mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">Ainda sem catálogos</h3>
            <p className="text-stone-500 max-w-md mb-5">
              Os catálogos carregados em Configurações &gt; Gerir Catálogos vão aparecer aqui.
            </p>
            <Button
              onClick={() => navigate('/profile', { state: { openSection: 'catalog' } })}
              className="bg-[#009392] hover:bg-[#007a79] text-white gap-2"
              data-testid="catalogos-empty-goto-settings-btn"
            >
              <Gear size={16} weight="bold" /> Ir para Gerir Catálogos
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="catalogos-grid">
            {catalogs.map((cat) => (
              <div
                key={cat.id}
                className="group border border-stone-200 rounded-xl overflow-hidden bg-white hover:shadow-lg hover:border-[#009392]/40 transition-all duration-200"
                data-testid={`catalogo-card-${cat.id}`}
              >
                <button
                  onClick={() => setViewerCatalog(cat)}
                  className="w-full text-left block"
                  data-testid={`btn-open-catalogo-${cat.id}`}
                >
                  <div className="aspect-[3/4] bg-stone-50 flex items-center justify-center overflow-hidden relative">
                    {cat.cover_gridfs_id ? (
                      <img
                        src={fileUrl(cat.cover_gridfs_id)}
                        alt={cat.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FilePdf size={56} className="text-red-500" weight="duotone" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 bg-white/95 text-[#009392] text-xs font-semibold px-3 py-1.5 rounded-full shadow">
                        <ArrowSquareOut size={14} weight="bold" /> Abrir
                      </span>
                    </div>
                  </div>
                </button>
                <div className="p-3">
                  <p className="text-sm font-medium text-stone-700 line-clamp-2 mb-2" title={cat.title}>{cat.title}</p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => shareByEmail(cat)}
                      className="flex-1 flex items-center justify-center py-1.5 text-sky-500 hover:bg-sky-50 rounded-md transition-colors"
                      data-testid={`btn-share-email-catalogo-${cat.id}`}
                      title="Partilhar por Email"
                    >
                      <EnvelopeSimple size={15} />
                    </button>
                    <button
                      onClick={() => shareByOutlook(cat)}
                      className="flex-1 flex items-center justify-center py-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      data-testid={`btn-share-outlook-catalogo-${cat.id}`}
                      title="Partilhar por Outlook"
                    >
                      <MicrosoftOutlookLogo size={15} />
                    </button>
                    <button
                      onClick={() => shareByWhatsapp(cat)}
                      className="flex-1 flex items-center justify-center py-1.5 text-green-500 hover:bg-green-50 rounded-md transition-colors"
                      data-testid={`btn-share-whatsapp-catalogo-${cat.id}`}
                      title="Partilhar por WhatsApp"
                    >
                      <WhatsappLogo size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visor de PDF em modal grande - iframe usa o motor nativo do navegador (rápido, com
          zoom por gesto de pinça no iPad), sem carregar nenhuma biblioteca extra */}
      <Dialog open={!!viewerCatalog} onOpenChange={(open) => !open && setViewerCatalog(null)}>
        <DialogContent className="max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="catalogo-viewer-dialog">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 flex-shrink-0">
            <p className="font-semibold text-stone-800 truncate pr-8">{viewerCatalog?.title}</p>
            <a
              href={viewerCatalog ? fileUrl(viewerCatalog.gridfs_id) : '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#009392] hover:underline inline-flex items-center gap-1 flex-shrink-0"
              data-testid="catalogo-viewer-open-new-tab"
            >
              <ArrowSquareOut size={13} /> Abrir em novo separador
            </a>
          </div>
          {viewerCatalog && (
            <iframe
              src={fileUrl(viewerCatalog.gridfs_id)}
              title={viewerCatalog.title}
              className="flex-1 w-full border-0"
              data-testid="catalogo-viewer-iframe"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
